import PhoneIconFilled from "../../assets/svg/v1/phone-icon-filled";
import CloseIcon from "../../assets/svg/close-icon";
import DialerDotsIcon from "../../assets/svg/dialer-dots-icon";
import PlusFilledIcon from "../../assets/svg/plus-filled-icon";
import Button from "../Button/Button";
import CircularLoader from "../Loader/CircularLoader";
import { InputLabelProps, InputProps } from "./types";

export const InputLabel: React.FC<InputLabelProps> = ({
  label,
  required = false,
  inputId,
  labelClassName,
  requiredIndicatorClassName,
}) => {
  return (
    <label htmlFor={inputId} className={labelClassName}>
      {label}
      {required && (
        <span className={requiredIndicatorClassName}>*</span>
      )}
    </label>
  );
};

const Input: React.FC<InputProps> = ({
  label,
  name,
  id,
  type = "text",
  required = false,
  disabled,
  placeholder,
  autoComplete,
  value,
  error,
  errorMessage,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  onLeftIconClick,
  onRightIconClick,
  onChange,
  onBlur,
  onKeyDown,
  onPaste,
  containerClassName = "",
  secondaryContainerClassName = "",
  inputClassName = "",
  iconClassName = "",
  labelClassName = "",
  requiredIndicatorClassName,
  errorClassName = "",
  showLoader = false,
  loaderClassName = "",
  showEmailButton = false,
  onEmailButtonClick,
  showCallButton = false,
  onCallButtonClick,
  onDialerButtonClick,
  showClearButton = false,
  onClearButtonClick,
  showCounter = false,
  maxLength = 0,
  min,
}) => {
  const inputId =
    id || name || `input-${Math.random().toString(36).slice(2, 11)}`;
  const errorId = `${inputId}-error`;

  return (
    <div className={`flex flex-col gap-1 ${containerClassName}`}>
      {label && (
        <InputLabel
          label={label}
          required={required}
          inputId={inputId}
          labelClassName={labelClassName}
          requiredIndicatorClassName={requiredIndicatorClassName}
        />
      )}
      <div className={`flex items-center gap-2 ${secondaryContainerClassName}`}>
        {LeftIcon && (
          <button
            type="button"
            aria-label="left icon button"
            onClick={onLeftIconClick}
            className={`shrink-0 ${name === "setting_value" ? "flex items-center" : ""} ${iconClassName}`}
          >
            <LeftIcon className={iconClassName} />
          </button>
        )}
        <input
          id={inputId}
          name={name}
          type={type}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          value={value}
          autoComplete={autoComplete}
          min={min}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error && errorMessage ? errorId : undefined}
          className={`w-full bg-transparent outline-none flex-1 min-w-0 border-none font-normal text-md text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder placeholder:font-normal placeholder:text-md ${inputClassName}`}
        />

        {showCounter ? (
          <span
            className={`text-xxs font-normal text-dark-gray dark:text-dark-gray-dk flex items-center justify-end ${
              value?.length && value?.length >= maxLength
                ? "text-verba-red-alert dark:text-verba-red-alert-dk"
                : ""
            }`}
          >
            {value?.length || 0}/{maxLength}
          </span>
        ) : null}
        {showEmailButton && (
          <Button
            innerText="Draft an email"
            buttonContainerClass="w-fit"
            innerTextClass="text-verba-indigo dark:text-verba-indigo-dk text-md mr-2"
            Icon={PlusFilledIcon}
            iconLeft={true}
            iconClass="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk"
            onClick={onEmailButtonClick}
          />
        )}
        {showCallButton && (
          <div className="flex items-center gap-2">
            <Button
              innerText="Dialer"
              buttonContainerClass="w-fit"
              innerTextClass="text-verba-indigo dark:text-verba-indigo-dk text-md mr-2"
              Icon={DialerDotsIcon}
              iconLeft={true}
              iconClass="w-[12px] h-[12px] text-verba-indigo dark:text-verba-indigo-dk"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDialerButtonClick?.();
              }}
            />

            <div className="w-px h-4 bg-border-gray dark:bg-border-gray-dk"></div>
            <Button
              innerText="Make a call"
              buttonContainerClass="w-fit"
              innerTextClass="text-verba-indigo dark:text-verba-indigo-dk text-md mr-2"
              Icon={PhoneIconFilled}
              iconLeft={true}
              iconClass="w-[12px] h-[12px] text-verba-indigo dark:text-verba-indigo-dk"
              onClick={onCallButtonClick}
            />
          </div>
        )}
        {showClearButton && (
          <button
            className="w-[16px] h-[16px] bg-gray-100 dark:bg-gray-100-dk rounded-full flex flex-col items-center justify-center"
            aria-label="close create user modal"
            onClick={onClearButtonClick}
            type="button"
          >
            <CloseIcon className="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        )}
        {RightIcon && (
          <button
            type="button"
            aria-label="right icon button"
            onClick={onRightIconClick}
            className={`${name === "setting_value" ? "flex items-center shrink-0" : "shrink-0"} ${iconClassName}`}
          >
            <RightIcon className={iconClassName} />
          </button>
        )}
        {showLoader && <CircularLoader className={loaderClassName} />}
      </div>

      {error && errorMessage && (
        <div
          id={errorId}
          role="alert"
          className={`text-xs text-verba-red-alert dark:text-verba-red-alert-dk ${errorClassName}`}
        >
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
};

export default Input;
